import React, { useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  Tooltip,
  Polygon,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  focusLabel,
  formatAcquisition,
  confidenceLabel,
} from "../../../../../utils/alertFocusPoints";

const COLOR_INSIDE = "#dc2626";  // foco dentro da área
const COLOR_OUTSIDE = "#f97316"; // foco fora da área
const COLOR_ALERT = "#2563eb";   // local do alerta
const COLOR_AREA = "#16a34a";    // polígono da propriedade

const parseCoords = (coords) => {
  if (!coords || coords === "-") return null;
  const [lat, lng] = coords.split(",").map((v) => Number(v.trim()));
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
};

// "POLYGON ((lon lat, lon lat, ...))" -> [[ [lat, lon], ... ]]
const wktToLatLngs = (wkt) => {
  if (!wkt || typeof wkt !== "string") return null;
  const rings = [...wkt.matchAll(/\(([^()]+)\)/g)]
    .map((m) =>
      m[1]
        .split(",")
        .map((pair) => pair.trim().split(/\s+/).map(Number))
        .filter(([lon, lat]) => Number.isFinite(lat) && Number.isFinite(lon))
        .map(([lon, lat]) => [lat, lon])
    )
    .filter((ring) => ring.length >= 3);
  return rings.length ? rings : null;
};

function FitBounds({ positions }) {
  const map = useMap();
  React.useEffect(() => {
    if (!positions.length) return;
    if (positions.length === 1) {
      map.setView(positions[0], 14);
      return;
    }
    map.fitBounds(L.latLngBounds(positions), { padding: [30, 30], maxZoom: 15 });
  }, [map, positions]);
  return null;
}

export default function FocusPointsMap({ points, alertCoordinates, areaWkts = [], height = 380 }) {
  const alertPos = useMemo(() => parseCoords(alertCoordinates), [alertCoordinates]);

  // cada WKT vira uma lista de anéis; juntamos todos numa lista de polígonos
  const areaPolygons = useMemo(
    () => areaWkts.map(wktToLatLngs).filter(Boolean),
    [areaWkts]
  );

  const validPoints = useMemo(
    () =>
      points
        .map((p, i) => ({ ...p, _pos: [Number(p.lat), Number(p.lon)], _index: i }))
        .filter((p) => Number.isFinite(p._pos[0]) && Number.isFinite(p._pos[1])),
    [points]
  );

  const allPositions = useMemo(
    () => [
      ...validPoints.map((p) => p._pos),
      ...(alertPos ? [alertPos] : []),
      ...areaPolygons.flat(2),
    ],
    [validPoints, alertPos, areaPolygons]
  );

  if (!allPositions.length) return null;

  return (
    <div style={{ height, width: "100%", borderRadius: 12, overflow: "hidden" }}>
      <MapContainer
        center={allPositions[0]}
        zoom={13}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds positions={allPositions} />
        
        {areaPolygons.map((rings, i) => (
          <Polygon
            key={i}
            positions={rings}
            pathOptions={{
              color: COLOR_AREA,
              weight: 2,
              fillColor: COLOR_AREA,
              fillOpacity: 0.12,
            }}
          >
            <Tooltip sticky>Propriedade (KML)</Tooltip>
          </Polygon>
        ))}

        {alertPos && (
          <CircleMarker
            center={alertPos}
            radius={9}
            pathOptions={{ color: "#fff", weight: 2, fillColor: COLOR_ALERT, fillOpacity: 1 }}
          >
            <Tooltip>Local do alerta</Tooltip>
          </CircleMarker>
        )}

        {validPoints.map((p) => {
          const color = p.inside_area ? COLOR_INSIDE : COLOR_OUTSIDE;
          return (
            <CircleMarker
              key={`${p.lat}-${p.lon}-${p._index}`}
              center={p._pos}
              radius={7}
              pathOptions={{ color, weight: 2, fillColor: color, fillOpacity: 0.6 }}
            >
              <Popup>
                <strong>Foco {p._index + 1}</strong>
                {focusLabel(p) && <div>{focusLabel(p)}</div>}
                {(p.acq_date || p.acq_time) && (
                  <div>Detecção: {formatAcquisition(p.acq_date, p.acq_time)}</div>
                )}
                {p.satellite && <div>Satélite: {p.satellite}</div>}
                {p.confidence != null && <div>Confiança: {confidenceLabel(p.confidence)}</div>}
                {p.frp != null && <div>FRP: {p.frp} MW</div>}
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}